import React, { useContext, useEffect, useState } from "react";
import { ShareContext } from "../../context/ShareContext";
import BackLoader from "../../components/backloader";
import JisButton from "../../components/controls/jisButton";
import JisDialogForm from "../../components/formdialog";
import { api, fmtDateTime, money } from "./hctc_common";
import "./hctc.css";

// Assessment rates (spec 16), screen svc_rates. Mapping gazette lines makes the per-unit rate the sum of those lines and
// splits the bill into the internal and external slips by each line's item type; without lines the rate and slip below are used.
export default function ServiceRates() {
  const { token, setIsExpired } = useContext(ShareContext);
  const [items, setItems] = useState([]);
  const [missing, setMissing] = useState([]);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    const d = await api(token, setIsExpired, "serviceapp/rates");
    setLoading(false);
    if (d.code === 0) {
      setItems(d.items || []);
      setMissing(d.missing || []);
    } else if (d.code !== -10) setErr(d.status || "Unable to load the rates");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (r) => {
    setErr("");
    setLoading(true);
    const d = await api(token, setIsExpired, "serviceapp/rates", { method: "POST", body: r });
    setLoading(false);
    if (d.code === 0) {
      setEdit(null);
      setMsg("Rate saved");
      load();
    } else if (d.code !== -10) setErr(d.status || "Unable to save");
  };

  return (
    <div className="hctc-page">
      <h2>Service Rates</h2>
      <div className="hctc-sub">Fees apply the same way to historical and JIS cases. Changes apply to assessments made after saving.</div>
      {msg && <div className="hctc-ok">{msg}</div>}
      {err && <div className="hctc-err">{err}</div>}
      <div className="table">
        <div className="table-wrapper" style={{ overflowX: "auto" }}>
          <table className="case-table hctc-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Unit</th>
                <th>Effective rate</th>
                <th>Internal / External per unit</th>
                <th>Gazette lines</th>
                <th>Status</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.title}
                    <div className="hctc-muted">{r.code}</div>
                  </td>
                  <td>{r.unit}</td>
                  <td>{money(r.effectiveRate)}</td>
                  <td>
                    {money(r.internalPerUnit)} / {money(r.externalPerUnit)}
                  </td>
                  <td className="wrap">
                    {r.lines.length === 0 ? (
                      <span className="hctc-muted">None: {r.defaultItemType} slip at {money(r.rate)}</span>
                    ) : (
                      r.lines.map((l) => (
                        <div key={l.id}>
                          {l.title} ({money(l.fee)}, {l.itemType}
                          {l.revenueCode ? `, ${l.revenueCode}` : ""})
                        </div>
                      ))
                    )}
                  </td>
                  <td>{r.isActive ? <span className="hctc-pill good">Active</span> : <span className="hctc-pill plain">Inactive (defaults used)</span>}</td>
                  <td className="hctc-muted">{fmtDateTime(r.updatedOn)}</td>
                  <td>
                    <JisButton variant="plain" onClick={() => setEdit({ ...r, gazetteLines: r.lines })}>
                      Edit
                    </JisButton>
                  </td>
                </tr>
              ))}
              {missing.map((m) => (
                <tr key={m.code}>
                  <td>
                    {m.title}
                    <div className="hctc-muted">{m.code}</div>
                  </td>
                  <td>{m.unit}</td>
                  <td>{money(m.rate)} (default)</td>
                  <td colSpan={4} className="hctc-muted">
                    Not configured yet; the default applies.
                  </td>
                  <td>
                    <JisButton variant="plain" onClick={() => setEdit({ id: 0, code: m.code, title: m.title, unit: m.unit, rate: m.rate, defaultItemType: "Internal", isActive: true, gazetteLines: [] })}>
                      Configure
                    </JisButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {edit && <RateDialog token={token} setIsExpired={setIsExpired} rate={edit} onClose={() => setEdit(null)} onSave={save} />}
      <BackLoader open={loading} />
    </div>
  );
}

function RateDialog({ token, setIsExpired, rate, onClose, onSave }) {
  const [r, setR] = useState(rate);
  const [lines, setLines] = useState(rate.gazetteLines || []);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const search = async () => {
    const d = await api(token, setIsExpired, `serviceapp/gazette-lines?search=${encodeURIComponent(q)}`);
    if (d.code === 0) setFound(d.list || []);
  };
  const perUnit = lines.reduce((s, l) => s + Number(l.fee || 0), 0);
  return (
    <JisDialogForm title={`Rate: ${r.title}`} onClose={onClose} minWidth="50%">
      <div className="hctc-dialog-body">
        <div className="hctc-grid">
          <div className="hctc-field span2">
            <label>Title</label>
            <input value={r.title} onChange={(e) => setR({ ...r, title: e.target.value })} />
          </div>
          <div className="hctc-field">
            <label>Unit</label>
            <select value={r.unit} onChange={(e) => setR({ ...r, unit: e.target.value })}>
              {["page", "day", "year", "fixed"].map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="hctc-field">
            <label>Rate (when no gazette line is mapped)</label>
            <input type="number" min="0" value={r.rate} onChange={(e) => setR({ ...r, rate: e.target.value })} />
          </div>
          <div className="hctc-field">
            <label>Slip (when no gazette line is mapped)</label>
            <select value={r.defaultItemType} onChange={(e) => setR({ ...r, defaultItemType: e.target.value })}>
              <option>Internal</option>
              <option>External</option>
            </select>
          </div>
          <div className="hctc-field" style={{ display: "flex", alignItems: "flex-end" }}>
            <label className="hctc-check">
              <input type="checkbox" checked={!!r.isActive} onChange={(e) => setR({ ...r, isActive: e.target.checked })} /> Active
            </label>
          </div>
        </div>
        <div className="hctc-section-title" style={{ marginTop: 14 }}>
          Gazette lines (per unit: {money(perUnit)})
        </div>
        {lines.map((l) => (
          <div key={l.id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
            <span>
              {l.title} - {money(l.fee)} <span className="hctc-pill plain">{l.itemType}</span>
            </span>
            <JisButton variant="plain" onClick={() => setLines(lines.filter((x) => x.id !== l.id))}>
              Remove
            </JisButton>
          </div>
        ))}
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input style={{ flex: 1, padding: 8, borderRadius: 8 }} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="find gazette line by title or revenue code" />
          <JisButton variant="outline" onClick={search}>
            Find
          </JisButton>
        </div>
        {found.length > 0 && (
          <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 6 }}>
            {found
              .filter((g) => !lines.some((l) => l.id === g.id))
              .map((g) => (
                <div key={g.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "4px 0", borderBottom: "1px dashed var(--border, #e2e8f0)" }}>
                  <span>
                    {g.title} - {money(g.fee)} ({g.itemType}
                    {g.revenueCode ? `, ${g.revenueCode}` : ""})
                  </span>
                  <JisButton variant="plain" onClick={() => setLines([...lines, g])}>
                    Add
                  </JisButton>
                </div>
              ))}
          </div>
        )}
        <div className="hctc-actions" style={{ marginTop: 14 }}>
          <JisButton onClick={() => onSave({ id: r.id, code: r.code, title: r.title, unit: r.unit, rate: Number(r.rate) || 0, defaultItemType: r.defaultItemType, isActive: !!r.isActive, gazetteLineIds: lines.map((l) => l.id) })}>
            Save
          </JisButton>
          <JisButton variant="outline" onClick={onClose}>
            Cancel
          </JisButton>
        </div>
      </div>
    </JisDialogForm>
  );
}
